// WHAT THE PO SAYS ABOUT ITSELF, WEIGHED AGAINST WHAT THIS QUOTE SAYS.
//
// ⚠️ EVERY WARNING HERE IS AMBER AND NONE OF THEM BLOCKS ANYTHING. That is
// the whole posture of this module and it is not a styling note: a PO is the
// customer's own paperwork, and a difference between their document and our
// quote is a fact for a person to judge, not an error for a machine to refuse.
// A blocking check here would stop somebody recording a real order because a
// company writes its own name with "Pvt." rather than "Private".
//
// ⚠️ AND NONE OF THEM CHANGES ANYTHING. No warning corrects a value, fills a
// field, or picks a side. `poHeaderWarnings` and `poLineWarnings` are pure
// functions of what was read plus what the quote already holds; the screen
// renders what they return and computes nothing itself. Same split as
// po_comparison.py deciding verdicts while the panel only draws them.
//
// ⚠️ NOTHING HERE READS OR WRITES A CUSTOMER. A mismatch between the PO's
// printed customer and the quote's is reported and nothing else -- it never
// creates a res.partner, never renames one, and never offers to. The
// customer's paperwork is their statement about themselves, not an
// instruction to rewrite our master. `_PO_HEADER_FIELDS` on the backend is
// the other half of that rule.
//
// ⚠️ AN UNKNOWN IS NOT A DISAGREEMENT, and every rule below is written that
// way. If the PO did not print a currency, or the quote has no recorded
// customer, there is nothing to differ FROM -- warning on an absent value
// would put an amber bar on most documents and train people past all of them.
// This is the same call `po_comparison.compare` makes when it refuses only on
// two KNOWN, different currencies.

export type PoWarningCode =
  | "customer_name_differs"
  | "vendor_not_ours"
  | "currency_differs"
  | "po_revised"
  | "po_currency_unrecognised"
  | "po_currency_unchecked";

export interface PoWarning {
  code: PoWarningCode;
  /** One sentence, for an amber strip. States the difference; proposes nothing. */
  message: string;
}

/** The header facts a warning can be drawn from — exactly as printed. */
export interface PoHeaderFacts {
  customer_name?: string | null;
  vendor_name?: string | null;
  currency?: string | null;
  po_revision?: string | null;
}

/** What this quote already holds, to weigh the document against. */
export interface QuoteFacts {
  /** The quote's recorded partner name, from the backend. */
  customerName?: string | null;
  /** The currency the quote's figures are in, from the backend. */
  currency?: string | null;
  /** Our own company name, from Settings. */
  companyName?: string | null;
  /** Our entity names as a customer PO may print them, from Settings >
   *  Company (`our_entity_aliases`, configurable identity P1-6). */
  ourEntityAliases?: string[] | null;
}

/** Trim, collapse runs of whitespace, casefold. Typography only.
 *
 *  Used by the VENDOR check, whose rule the owner confirmed unchanged on
 *  2026-09-17 (Settings company name, or contains one of our entity names in
 *  any casing -- Settings > Company since P1-6).
 *  The CUSTOMER check uses `sameCompany` below, which goes further -- see the
 *  note there for why the two are deliberately not the same comparison. */
function normalise(value: string | null | undefined): string {
  if (typeof value !== "string") return "";
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

/** The legal-form words this comparison treats as the same word.
 *
 *  ⚠️ EXACTLY TWO PAIRS, BECAUSE EXACTLY TWO WERE ASKED FOR. Pvt/Private and
 *  Ltd/Limited. This is not the start of an open-ended abbreviation table:
 *  every entry added here makes two more company names compare EQUAL, and the
 *  failure mode is silent -- a genuine mismatch stops being reported. A new
 *  pair needs a real document that motivated it. */
const LEGAL_FORMS: Record<string, string> = {
  pvt: "private",
  "pvt.": "private",
  private: "private",
  ltd: "limited",
  "ltd.": "limited",
  limited: "limited",
};

/** Is every token here a legal form and nothing else? */
function onlyLegalForms(tokens: string[]): boolean {
  return tokens.length > 0 && tokens.every((t) => LEGAL_FORMS[t] !== undefined);
}

function tokenise(value: string): string[] {
  return value
    .toLowerCase()
    // ⚠️ PUNCTUATION BECOMES A SPACE, NOT NOTHING. Removing it outright would
    // glue "Larkspur India Pvt.Ltd" into one token; a space keeps the words.
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => LEGAL_FORMS[t] ?? t);
}

/** A company name split into the ENTITY and whatever followed the comma.
 *
 *  ⚠️ A SUFFIX AFTER A COMMA IS A SITE OR DIVISION, NOT A DIFFERENT COMPANY.
 *  Owner's ruling, 2026-09-17, from the real document: the PO prints
 *  "Larkspur India Private Limited, IDC" while the quote's customer is "Larkspur
 *  India Private Limited". Those are one company and one of its sites, and
 *  warning on the pair puts an amber bar on a correctly-read PO.
 *
 *  ⚠️ BUT THE COMMA MAY ALSO PRECEDE THE LEGAL FORM: "Larkspur India, Private
 *  Limited" is the same company written differently, not a site called
 *  "Private Limited". When the part after the comma is made up ONLY of legal
 *  forms, it is folded back into the entity. */
export function companyParts(name: string | null | undefined): {
  entity: string; site: string;
} {
  const raw = typeof name === "string" ? name.trim() : "";
  if (!raw) return { entity: "", site: "" };

  const comma = raw.indexOf(",");
  if (comma === -1) return { entity: tokenise(raw).join(" "), site: "" };

  const head = tokenise(raw.slice(0, comma));
  const tail = tokenise(raw.slice(comma + 1));
  if (onlyLegalForms(tail)) {
    return { entity: head.concat(tail).join(" "), site: "" };
  }
  return { entity: head.join(" "), site: raw.slice(comma + 1).trim() };
}

/** Do these two names denote the same company?
 *
 *  ⚠️ THE ENTITY PART MUST MATCH EXACTLY AFTER NORMALISATION. There is
 *  deliberately NO prefix, substring or "contains" test here, and that is the
 *  difference between this and `vendorIsOurs`. "Kestrel" and "Kestrel Consultancy
 *  Services" are different companies; a containment test would call them the
 *  same and the warning would stop firing on the case it exists for. The
 *  vendor check may use containment because being wrong there costs an amber
 *  bar on our own paperwork; being wrong here means a PO is filed against the
 *  wrong customer with nothing on screen to say so.
 *
 *  ⚠️ AN UNKNOWN ON EITHER SIDE IS NOT A DISAGREEMENT.
 *
 *  ⚠️ THIS SUPERSEDES AN EARLIER, OPPOSITE JUDGEMENT IN THIS FILE. It used to
 *  say that a normaliser confident enough to equate legal suffixes was
 *  confident enough to equate "Contoso Cabling" with "Contoso Cables", and
 *  that a legal-suffix difference should therefore warn. The owner decided
 *  otherwise on 2026-09-17, against a real document. The old reasoning's
 *  actual concern is still honoured: nothing here equates two DIFFERENT
 *  names, because only whole-entity equality passes -- what changed is that
 *  "Pvt" and "Private" are now understood to be the same word, which is a
 *  fact about English abbreviation, not a guess about company identity. */
export function sameCompany(
  a: string | null | undefined, b: string | null | undefined,
): boolean {
  const left = companyParts(a);
  const right = companyParts(b);
  if (!left.entity || !right.entity) return true;
  return left.entity === right.entity;
}

/** Whether a PO's vendor is us.
 *
 *  ⚠️ CONFIRMED BY THE OWNER, 2026-09-17: a PO addressed to our legal entity
 *  under any of its spellings is addressed to us. The rule is: the vendor is
 *  ours if it matches our Settings company name OR contains one of OUR ENTITY
 *  NAMES (Settings > Company, one per line; it was a compiled-in constant
 *  until configurable identity P1-6) in any casing. It is a WARNING either
 *  way, so being wrong about it costs an amber bar, never a refused PO. */
export function vendorIsOurs(
  vendorName: string | null | undefined, companyName: string | null | undefined,
  ourEntityAliases: readonly string[] | null | undefined = [],
): boolean {
  const vendor = normalise(vendorName);
  if (!vendor) return true;               // unknown is not a disagreement
  for (const alias of ourEntityAliases ?? []) {
    const marker = normalise(alias);
    if (marker && vendor.includes(marker)) return true;
  }
  const company = normalise(companyName);
  // ⚠️ AN UNKNOWN COMPANY NAME CANNOT CONVICT ON ITS OWN. If Settings has not
  // been read (or the read failed), there is no name and no entity list to
  // compare against, so a named vendor is reported -- an amber bar, never a
  // refused PO.
  if (!company) return false;
  // Either containing the other counts: a PO prints the full legal entity
  // where Settings often holds the trading name, and vice versa.
  return vendor === company || vendor.includes(company) || company.includes(vendor);
}

/** Whether the printed revision says this is not the original PO.
 *
 *  ⚠️ `po_revision` IS TEXT, NOT A NUMBER, and the model says why: a PO may
 *  print "0", "2", or "0A". So:
 *    * absent or empty            -> nothing to say
 *    * parses as a number, <= 0   -> the original PO, no warning
 *    * parses as a number, > 0    -> "revision N"
 *    * does not parse, not "0"    -> reported with the text AS PRINTED, since
 *                                    a revision label that is not zero is not
 *                                    the original and we must not invent a
 *                                    number for it. */
export function revisionAbove0(revision: string | null | undefined): string | null {
  const raw = (revision ?? "").trim();
  if (!raw) return null;
  const n = Number(raw);
  if (Number.isFinite(n)) return n > 0 ? String(n) : null;
  return raw === "0" ? null : raw;
}

export function poHeaderWarnings(
  header: PoHeaderFacts | null | undefined, quote: QuoteFacts | null | undefined,
): PoWarning[] {
  const h = header ?? {};
  const q = quote ?? {};
  const out: PoWarning[] = [];

  // (1) THE CUSTOMER. Both sides must be known before they can differ.
  const poCustomer = (h.customer_name ?? "").trim();
  const quoteCustomer = (q.customerName ?? "").trim();
  if (poCustomer && quoteCustomer && !sameCompany(poCustomer, quoteCustomer)) {
    out.push({
      code: "customer_name_differs",
      message:
        `This PO names ${poCustomer}, and the quote's customer is `
        + `${quoteCustomer}. Nothing has been changed on either — check that `
        + `this PO belongs to this quote.`,
    });
  }

  // (2) THE VENDOR. Us, or somebody else's paperwork filed here by mistake.
  const vendor = (h.vendor_name ?? "").trim();
  if (vendor && !vendorIsOurs(vendor, q.companyName, q.ourEntityAliases)) {
    out.push({
      code: "vendor_not_ours",
      message:
        `This PO is addressed to ${vendor}, which is neither our company name `
        + `nor one of our entity names. It may be a PO for a different supplier.`,
    });
  }

  // (3) THE CURRENCY. Only two KNOWN, different codes -- the same rule the
  // comparison itself applies before it refuses.
  const poCurrency = (h.currency ?? "").trim().toUpperCase();
  const quoteCurrency = (q.currency ?? "").trim().toUpperCase();
  if (poCurrency && quoteCurrency && poCurrency !== quoteCurrency) {
    out.push({
      code: "currency_differs",
      message:
        `This PO is in ${poCurrency} and this quote is priced in `
        + `${quoteCurrency}. Nothing will be converted — the comparison `
        + `refuses to convert between currencies.`,
    });
  }

  // (4) THE REVISION THE CUSTOMER PRINTED. Not a revision history of ours.
  const revision = revisionAbove0(h.po_revision);
  if (revision) {
    out.push({
      code: "po_revised",
      message:
        `Revised PO — revision ${revision}. Check that this is the latest `
        + `version the customer sent.`,
    });
  }

  return out;
}

/** One shipment as the Oracle profile prints it. */
export interface PoShipment {
  quantity?: string | null;
  extended?: string | null;
  date?: string | null;
  [key: string]: unknown;
}

/** The per-line warning: several shipments, so no single printed quantity.
 *
 *  ⚠️ THE QUANTITY IS LEFT FOR A PERSON AND THE SHIPMENTS ARE LISTED. It is
 *  never summed — owner's ruling, and the right one: a sum is a figure printed
 *  on no page of the customer's document, and it would be indistinguishable
 *  on screen from one that was read. The row's quantity cell stays EMPTY,
 *  which the existing save check already refuses until somebody fills it. */
export function shipmentsNote(shipments: PoShipment[] | null | undefined): string | null {
  const list = shipments ?? [];
  if (list.length < 2) return null;
  const parts = list.map((s, i) => {
    const qty = (s.quantity ?? "").toString().trim() || "an unreadable quantity";
    const when = (s.date ?? "").toString().trim();
    const ext = (s.extended ?? "").toString().trim();
    return `${i + 1}: ${qty}${when ? ` on ${when}` : ""}${ext ? ` (${ext})` : ""}`;
  });
  return (
    `This line has ${list.length} shipments, so the document prints no single `
    + `quantity for it. Type the quantity yourself — the shipments are not `
    + `added up. Shipments — ${parts.join("; ")}.`
  );
}

/** ⚠️ IS THE PRINTED CURRENCY CODE ONE THIS DEPLOYMENT ACTUALLY HAS?
 *
 *  The save route resolves a currency BY NAME and **raises** on a code it
 *  cannot find, so a code this database has never adopted would fail the whole
 *  save. The screen therefore checks first and sends nothing when it cannot
 *  match -- the PO records no currency rather than the person losing the save.
 *
 *  ⚠️ EXACT MATCH ONLY, CASE-FOLDED. No nearest-neighbour, no prefix, no
 *  "RS -> INR" table. A currency is the denomination of every figure on the
 *  document; guessing it wrong is worse in kind than not recording it, because
 *  the comparison would then compare confidently across two denominations.
 *
 *  ⚠️ AND "WE COULD NOT CHECK" IS NOT "THE DOCUMENT IS WRONG". `listCurrencies`
 *  returns [] both when the deployment has no currencies and when the read
 *  FAILED, and those must not be reported with the same sentence: one blames
 *  the customer's document, the other admits our own gap. Two codes, two
 *  messages, and in both cases the currency is left unsent.
 *
 *  Returns null when the code matches, and also when the document printed no
 *  currency at all -- an absent currency is not a problem to report. */
export function currencyRecognition(
  printed: string | null | undefined,
  knownCodes: string[] | null | undefined,
): { warning: PoWarning | null; send: string | null } {
  const code = (printed ?? "").trim().toUpperCase();
  if (!code) return { warning: null, send: null };

  const known = (knownCodes ?? []).map((c) => (c ?? "").trim().toUpperCase())
    .filter(Boolean);

  if (!known.length) {
    return {
      send: null,
      warning: {
        code: "po_currency_unchecked",
        message:
          `This PO is priced in ${code}, but the list of currencies this `
          + `system accepts could not be read, so the currency has not been `
          + `recorded on the PO. Everything else was saved.`,
      },
    };
  }

  if (known.includes(code)) return { warning: null, send: code };

  return {
    send: null,
    warning: {
      code: "po_currency_unrecognised",
      message:
        `PO currency not recognised: the document is priced in ${code}, which `
        + `is not a currency this system has. It has been left off the PO `
        + `rather than guessed — so the comparison will not be able to refuse `
        + `on a currency difference for this PO. Everything else was saved.`,
    },
  };
}

